/*
 * AAIKO PDP details drawer.
 * Figma 1210:25759 (desktop, 480px panel) and 1210:26546 (mobile, 300px panel).
 * The tab strip is undrawn: AAIKO asked for one panel with tabs, after COS.
 *
 * The markup ships as <details>/<summary>, which already works without JS.
 * This upgrade moves the one drawer to <body> so a fixed overlay is not trapped
 * by an ancestor `container-type` / `contain: layout` (both create a containing
 * block for position:fixed), moves each row's panel into it as a tabpanel, then
 * drives open/close itself: scrim, focus trap, Escape, scroll lock, focus
 * returned to the trigger.
 */
(function () {
  if (window.customElements && customElements.get('aaiko-details')) return;

  var FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  var scrim = null;
  var current = null; // { drawer, trigger }

  function getScrim() {
    if (scrim && scrim.isConnected) return scrim;
    scrim = document.createElement('div');
    scrim.className = 'aaiko-details__scrim';
    scrim.setAttribute('data-aaiko-details-scrim', '');
    scrim.addEventListener('click', function () {
      closeCurrent();
    });
    document.body.appendChild(scrim);
    return scrim;
  }

  // tabIndex -1 drops the unselected tabs: only the selected one is in the tab
  // order, so the others must not count as the first or last stop of the trap.
  function focusable(drawer) {
    return Array.prototype.filter.call(drawer.querySelectorAll(FOCUSABLE), function (el) {
      return el.tabIndex !== -1 && (el.offsetParent !== null || el === document.activeElement);
    });
  }

  function openDrawer(drawer, trigger) {
    getScrim().setAttribute('data-open', '');
    drawer.setAttribute('data-open', '');
    trigger.setAttribute('aria-expanded', 'true');
    document.documentElement.classList.add('aaiko-details-locked');
    current = { drawer: drawer, trigger: trigger };
    // Focus the dialog itself, not the close button: the trap starts at the
    // top of the panel and no focus ring is painted on a pointer-driven open.
    drawer.focus({ preventScroll: true });
  }

  function closeCurrent() {
    if (!current) return;
    var drawer = current.drawer;
    var trigger = current.trigger;
    current = null;
    drawer.removeAttribute('data-open');
    trigger.setAttribute('aria-expanded', 'false');
    if (scrim) scrim.removeAttribute('data-open');
    document.documentElement.classList.remove('aaiko-details-locked');
    trigger.focus({ preventScroll: true });
  }

  document.addEventListener('keydown', function (event) {
    if (!current) return;
    if (event.key === 'Escape' || event.key === 'Esc') {
      event.preventDefault();
      closeCurrent();
      return;
    }
    if (event.key !== 'Tab') return;
    var items = focusable(current.drawer);
    if (!items.length) {
      event.preventDefault();
      return;
    }
    var first = items[0];
    var last = items[items.length - 1];
    var active = document.activeElement;
    if (!current.drawer.contains(active) || active === current.drawer) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus();
      return;
    }
    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  });

  function AaikoDetails() {
    return Reflect.construct(HTMLElement, [], AaikoDetails);
  }
  AaikoDetails.prototype = Object.create(HTMLElement.prototype);
  AaikoDetails.prototype.constructor = AaikoDetails;
  Object.setPrototypeOf(AaikoDetails, HTMLElement);

  AaikoDetails.prototype.connectedCallback = function () {
    if (this.aaikoReady) return;
    this.aaikoReady = true;

    var drawer = this.querySelector('[data-aaiko-details-drawer]');
    if (!drawer) return;
    var strip = drawer.querySelector('[data-aaiko-details-tabs]');
    var tabs = Array.prototype.slice.call(strip.querySelectorAll('[role="tab"]'));

    Array.prototype.forEach.call(this.querySelectorAll('[data-aaiko-details-panel]'), function (panel) {
      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('tabindex', '0');
      drawer.appendChild(panel);
    });
    drawer.hidden = false;
    document.body.appendChild(drawer);
    this.setAttribute('data-aaiko-details-enhanced', '');

    // Same travel affordance as the FAQ strip: an edge with tabs behind it fades.
    function syncFade() {
      var max = strip.scrollWidth - strip.clientWidth;
      strip.toggleAttribute('data-aaiko-details-more-start', strip.scrollLeft > 1);
      strip.toggleAttribute('data-aaiko-details-more-end', strip.scrollLeft < max - 1);
    }

    function select(tab, moveFocus) {
      tabs.forEach(function (candidate) {
        var on = candidate === tab;
        candidate.setAttribute('aria-selected', on ? 'true' : 'false');
        candidate.setAttribute('tabindex', on ? '0' : '-1');
        document.getElementById(candidate.getAttribute('aria-controls')).hidden = !on;
      });
      document.getElementById(tab.getAttribute('aria-controls')).scrollTop = 0;
      // Not scrollIntoView: that also scrolls ancestors, and the closed drawer
      // sits translated off the right edge of the page.
      strip.scrollLeft = tab.offsetLeft - strip.offsetLeft - (strip.clientWidth - tab.offsetWidth) / 2;
      syncFade();
      if (moveFocus) tab.focus();
    }

    select(tabs[0]);
    strip.addEventListener('scroll', syncFade, { passive: true });

    strip.addEventListener('click', function (event) {
      var tab = event.target.closest('[role="tab"]');
      if (tab) select(tab);
    });

    strip.addEventListener('keydown', function (event) {
      var index = tabs.indexOf(document.activeElement);
      if (index === -1) return;
      var next;
      if (event.key === 'ArrowRight') next = tabs[(index + 1) % tabs.length];
      else if (event.key === 'ArrowLeft') next = tabs[(index - 1 + tabs.length) % tabs.length];
      else if (event.key === 'Home') next = tabs[0];
      else if (event.key === 'End') next = tabs[tabs.length - 1];
      if (!next) return;
      event.preventDefault();
      select(next, true);
    });

    Array.prototype.forEach.call(this.querySelectorAll('[data-aaiko-details-toggle]'), function (trigger) {
      function open(event) {
        event.preventDefault();
        select(document.getElementById(trigger.getAttribute('data-aaiko-details-toggle')));
        openDrawer(drawer, trigger);
      }

      trigger.addEventListener('click', open);
      trigger.addEventListener('keydown', function (event) {
        if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') open(event);
      });
    });

    drawer.querySelector('[data-aaiko-details-close]').addEventListener('click', function () {
      closeCurrent();
    });
  };

  customElements.define('aaiko-details', AaikoDetails);
})();
